import React from 'react';
import { Box, Typography, TextField, Button, IconButton, Chip } from '@mui/material';
import { computeAttemptStats, parseAttemptValue } from '../../utils/testStats';
import { derivedSpeedMph } from '../../utils/derivedSpeed';
import { unitLabel } from '../../utils/testUnits';

/**
 * One combine-style test within a testing session: N attempt inputs + Add
 * Attempt + live Best/Average/Worst/Spread, all derived from whatever's
 * currently typed — nothing here is stored, it's a preview of what the
 * derived numbers will be once saved.
 *
 * values: array of raw string inputs (kept as strings so a field can be
 * legitimately blank mid-entry without fighting the input).
 */
export default function AttemptInputGroup({ testType, values, onChange }) {
  const parsed = values.map(parseAttemptValue);
  const stats = computeAttemptStats(parsed.filter(v => v !== null), testType.better_direction);
  const unitDisplay = unitLabel(testType.unit);
  const recommended = testType.recommended_attempts || 3;

  const handleAttemptChange = (idx, raw) => {
    const next = [...values];
    next[idx] = raw;
    onChange(next);
  };

  const addAttempt = () => onChange([...values, '']);

  const removeAttempt = (idx) => {
    if (values.length <= 1) return; // always keep at least one slot
    onChange(values.filter((_, i) => i !== idx));
  };

  const invalidIdx = values
    .map((v, i) => ({ v, i }))
    .filter(({ v }) => v !== '' && v !== null && parseAttemptValue(v) === null)
    .map(({ i }) => i);

  // Phase C.7 — generalized from a forty_yard-only special case: any
  // elapsed-time sprint test with a distance-specific formula
  // (utils/derivedSpeed.js) gets a derived-at-display speed chip/value.
  // Never stored, never shared between tests of different distances.
  const bestMph = derivedSpeedMph(testType.key, stats.best);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {testType.display_name} <span style={{ color: '#6a8aa8', fontWeight: 500, fontSize: '0.85rem' }}>({unitDisplay})</span>
        </Typography>
        {stats.count > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Best ${stats.best}`} sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
            <Chip size="small" label={`Avg ${stats.average}`} sx={{ bgcolor: '#131318', color: '#9eb8d0', fontWeight: 700 }} />
            <Chip size="small" label={`Worst ${stats.worst}`} sx={{ bgcolor: '#2a1405', color: '#f5a623', fontWeight: 700 }} />
            <Chip size="small" label={`Spread ${stats.spread}`} sx={{ bgcolor: '#131318', color: '#6a8aa8', fontWeight: 700 }} />
            {bestMph !== null && (
              <Chip size="small" label={`Best Avg Speed ${bestMph} mph`} sx={{ bgcolor: '#0a1f2a', color: '#38bdf8', fontWeight: 700 }} />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {values.map((v, idx) => {
          const num = parseAttemptValue(v);
          const mph = derivedSpeedMph(testType.key, num);
          const canRemove = idx >= recommended; // the default/recommended slots stay clutter-free
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                inputProps={{ step: 0.01, min: 0, inputMode: 'decimal' }}
                label={`Attempt ${idx + 1}`}
                value={v}
                onChange={e => handleAttemptChange(idx, e.target.value)}
                error={invalidIdx.includes(idx)}
                helperText={invalidIdx.includes(idx) ? 'Must be a positive number' : ' '}
                sx={{ flex: 1, maxWidth: 200 }}
              />
              {mph !== null && (
                <Typography sx={{ fontSize: '0.8rem', color: '#38bdf8', minWidth: 68, mt: -1.5 }}>{mph} mph</Typography>
              )}
              {canRemove && (
                <IconButton
                  size="small"
                  aria-label={`Remove attempt ${idx + 1}`}
                  onClick={() => removeAttempt(idx)}
                  sx={{ color: '#6a8aa8', mt: -1.5 }}
                >
                  ✕
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

      <Button size="small" onClick={addAttempt} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}>
        + Add Attempt
      </Button>
    </Box>
  );
}
